import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    LinearProgress,
    Avatar,
    Chip,
    Paper,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Fade
} from '@mui/material';
import {
    LocalFireDepartment,
    ArrowForward,
    PlayCircleFilled,
    EmojiEvents,
    Timeline,
    TipsAndUpdates,
    School,
    MilitaryTech,
    Info,
    Star,
    Psychology,
    TrendingUp,
    ChevronLeft,
    ChevronRight,
    Map,
    PieChart,
    AccessTime,
    BarChart,
    ThumbUp,
    BugReport
} from '@mui/icons-material';
import authService from '../services/authService';

// --- RANK SYSTEM MOCK DATA & LOGIC ---
const RANKS = [
    { name: 'Recruit', minPoints: 0, icon: '🧱' },
    { name: 'Private', minPoints: 500, icon: '🛡️' },         // Easy start
    { name: 'Corporal', minPoints: 1500, icon: '🎖️' },       // +1000
    { name: 'Sergeant', minPoints: 3500, icon: '🎗️' },       // +2000 (Harder)
    { name: 'Staff Sergeant', minPoints: 7500, icon: '⭐⭐' }, // +4000 (Much harder)
    { name: 'Lieutenant', minPoints: 15000, icon: '⭐⭐⭐' }, // +7500
    { name: 'Captain', minPoints: 30000, icon: '🦅' },       // +15000
    { name: 'Major', minPoints: 60000, icon: '👑' },       // +30000
    { name: 'Colonel', minPoints: 100000, icon: '⚜️' },     // +40000
    { name: 'General', minPoints: 150000, icon: '🌟' }      // +50000 (Elite)
];

const POINT_RULES = [
    { action: 'Complete a Topic', points: '+10', icon: <School color="primary" /> },
    { action: 'Daily Login', points: '+5', icon: <LocalFireDepartment color="warning" /> },
    { action: 'Score 100% on Quiz', points: '+50', icon: <Star sx={{ color: '#FFD700' }} /> },
    { action: 'Complete a Course', points: '+500', icon: <EmojiEvents color="success" /> },
    { action: 'Help a Peer (Forum)', points: '+20', icon: <Psychology color="info" /> },
    { action: 'Rate Topic Quality', points: '+20', icon: <ThumbUp color="secondary" /> },
    { action: 'Report an Issue', points: '+50', icon: <BugReport color="error" /> }
];

const MOCK_USER_POINTS = 1450;

// --- CAROUSEL DATA ---
const MISSIONS = [
    {
        title: "Master Quadratics",
        desc: "Complete \"Quadratic Formulas\" today to earn <strong>+20 Bonus Points</strong>.",
        btnText: "Start Mission",
        icon: <PlayCircleFilled />,
        color: "#00d2d3"
    },
    {
        title: "Quiz Challenge",
        desc: "Score >80% on the <strong>Physics Motion Quiz</strong> to unlock a rare badge.",
        btnText: "Take Quiz",
        icon: <Star />,
        color: "#FFD700"
    },
    {
        title: "Help a Peer",
        desc: "Answer 2 questions in the <strong>Community Forum</strong> to earn +50 Karma Points.",
        btnText: "Go to Forum",
        icon: <Psychology />,
        color: "#a29bfe"
    }
];

const STATS_WIDGETS = [
    {
        label: "Weekly Goal",
        value: "75%",
        component: (
            <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#00d2d3" strokeWidth="8" strokeDasharray="283" strokeDashoffset="70" strokeLinecap="round" transform="rotate(-90 50 50)" />
                </svg>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h5" fontWeight="bold">75%</Typography>
                    <Typography variant="caption" align="center" sx={{ opacity: 0.7, lineHeight: 1 }}>Weekly<br />Goal</Typography>
                </Box>
            </Box>
        )
    },
    {
        label: "Study Time",
        value: "12h",
        component: (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120 }}>
                <AccessTime sx={{ fontSize: 50, color: '#ff7675', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">12.5h</Typography>
                <Typography variant="caption" align="center" sx={{ opacity: 0.7 }}>This Week</Typography>
            </Box>
        )
    },
    {
        label: "Topic Mastery",
        value: "A",
        component: (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120 }}>
                <BarChart sx={{ fontSize: 50, color: '#fdcb6e', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">Top 10%</Typography>
                <Typography variant="caption" align="center" sx={{ opacity: 0.7 }}>Class Rank</Typography>
            </Box>
        )
    }
];


const StudentDashboardV3: React.FC = () => {
    const navigate = useNavigate();
    const [openRules, setOpenRules] = useState(false);

    // Get User Data
    const user = authService.getCurrentUser();
    const studentName = user ? `${user.firstName} ${user.lastName}` : 'Cadet';

    // Carousel States
    const [missionIndex, setMissionIndex] = useState(0);
    const [statIndex, setStatIndex] = useState(0);

    const handleNextMission = () => setMissionIndex((prev) => (prev + 1) % MISSIONS.length);
    const handlePrevMission = () => setMissionIndex((prev) => (prev - 1 + MISSIONS.length) % MISSIONS.length);

    const handleNextStat = () => setStatIndex((prev) => (prev + 1) % STATS_WIDGETS.length);
    const handlePrevStat = () => setStatIndex((prev) => (prev - 1 + STATS_WIDGETS.length) % STATS_WIDGETS.length);

    // Calculate Rank
    const currentRankIndex = RANKS.findIndex((r, i) =>
        MOCK_USER_POINTS >= r.minPoints && (i === RANKS.length - 1 || MOCK_USER_POINTS < RANKS[i + 1].minPoints)
    );
    const currentRank = RANKS[currentRankIndex];
    const nextRank = RANKS[currentRankIndex + 1];

    // Progress to next rank
    let progress = 0;
    let pointsToNext = 0;
    if (nextRank) {
        const range = nextRank.minPoints - currentRank.minPoints;
        const currentInRank = MOCK_USER_POINTS - currentRank.minPoints;
        progress = (currentInRank / range) * 100;
        pointsToNext = nextRank.minPoints - MOCK_USER_POINTS;
    } else {
        progress = 100; // Max rank
    }

    const currentMission = MISSIONS[missionIndex];
    const currentStat = STATS_WIDGETS[statIndex];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>

            {/* 1. HERO SECTION */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 5,
                    borderRadius: 5,
                    background: 'linear-gradient(135deg, #1e272e 0%, #485460 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0px 10px 30px rgba(0,0,0,0.2)'
                }}
            >
                {/* Decorative Background Elements */}
                <MilitaryTech sx={{ position: 'absolute', top: -30, right: -30, fontSize: 300, opacity: 0.05, transform: 'rotate(15deg)' }} />

                <Grid container alignItems="center" spacing={2}>
                    {/* COLUMN 1: Rank & Identity (md=4) */}
                    <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid rgba(255,255,255,0.1)' }, pr: { md: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.1)', fontSize: 40, border: '2px solid #FFD700' }}>
                                {currentRank.icon}
                            </Avatar>
                            <Box>
                                <Typography variant="overline" color="rgba(255,255,255,0.7)" letterSpacing={1.5}>
                                    {studentName}
                                </Typography>
                                <Typography variant="h3" fontWeight="900" sx={{ color: '#FFD700', textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)' }}>
                                    {currentRank.name}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" fontWeight="bold">Global Rating: {MOCK_USER_POINTS}</Typography>
                                {nextRank && <Typography variant="body2" color="#00d2d3">{pointsToNext} to {nextRank.name}</Typography>}
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '& .MuiLinearProgress-bar': { bgcolor: '#00d2d3' }
                                }}
                            />
                        </Box>

                        <Button
                            startIcon={<Info />}
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none' }}
                            onClick={() => setOpenRules(true)}
                        >
                            Rank & Point System
                        </Button>
                    </Grid>

                    {/* COLUMN 2: Mission Carousel (md=5) */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ px: { md: 2 }, position: 'relative' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocalFireDepartment color="error" />
                                        <Typography variant="subtitle2" fontWeight="bold" color="#ff6b6b">
                                            DAY 5 STREAK
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label="#410 Global Rank"
                                        icon={<TrendingUp sx={{ fontSize: '16px !important', color: '#1e272e !important' }} />}
                                        sx={{ bgcolor: '#00d2d3', color: '#1e272e', fontWeight: 'bold' }}
                                    />
                                </Box>

                                {/* Carousel Controls */}
                                <Box>
                                    <IconButton size="small" onClick={handlePrevMission} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                        <ChevronLeft />
                                    </IconButton>
                                    <IconButton size="small" onClick={handleNextMission} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                        <ChevronRight />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Fade key={missionIndex} in={true} timeout={500}>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                                        {currentMission.title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ opacity: 0.8, mb: 3, minHeight: 48 }}
                                        dangerouslySetInnerHTML={{ __html: currentMission.desc }}
                                    />

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => navigate('/student-courses')}
                                            startIcon={currentMission.icon}
                                            sx={{ bgcolor: currentMission.color, color: '#1e272e', fontWeight: 'bold', borderRadius: 3, px: 3, '&:hover': { bgcolor: 'white' } }}
                                        >
                                            {currentMission.btnText}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            startIcon={<Map />}
                                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 3 }}
                                        >
                                            Course Map
                                        </Button>
                                    </Stack>
                                </Box>
                            </Fade>
                        </Box>
                    </Grid>

                    {/* COLUMN 3: Stats Carousel (md=3) */}
                    <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid rgba(255,255,255,0.1)', pl: { md: 2 } }}>
                        <Fade key={statIndex} in={true} timeout={500}>
                            <Box sx={{ mb: 2 }}>
                                {currentStat.component}
                            </Box>
                        </Fade>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={handlePrevStat} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                <ChevronLeft />
                            </IconButton>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 80, textAlign: 'center' }}>
                                {currentStat.label}
                            </Typography>
                            <IconButton size="small" onClick={handleNextStat} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Grid>

                </Grid>
            </Paper>

            {/* 2. ACTIVE COURSES */}
            <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Active Operations (Courses)
                    </Typography>
                    <Button endIcon={<ArrowForward />} color="inherit" onClick={() => navigate('/student-courses')}>
                        See All
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {/* Active Course 1 */}
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <Box>
                                    <Chip label="High Priority" size="small" sx={{ bgcolor: '#FF7675', color: 'white', fontWeight: 'bold', mb: 1, borderRadius: 2 }} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>Mathematics</Typography>
                                    <Typography variant="body2" color="text.secondary">Next Lesson: <strong>Quadratic Equations</strong></Typography>
                                </Box>
                                <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.50', color: 'primary.main' }}><School /></Avatar>
                            </Box>
                            <Box sx={{ px: 3, pb: 3, mt: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary">Progress (Module 3/5)</Typography>
                                    <Typography variant="caption" fontWeight="bold">60%</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={60} sx={{ height: 8, borderRadius: 4, mb: 3 }} />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate('/student-courses')}
                                    sx={{ borderRadius: 3, py: 1.2, fontWeight: 'bold', textTransform: 'none', fontSize: '1rem' }}
                                >
                                    Continue Lesson
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Active Course 2 */}
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <Box>
                                    <Chip label="On Track" size="small" sx={{ bgcolor: '#55EFC4', color: '#2D3436', fontWeight: 'bold', mb: 1, borderRadius: 2 }} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>Physics</Typography>
                                    <Typography variant="body2" color="text.secondary">Next Lesson: <strong>Motion 101</strong></Typography>
                                </Box>
                                <Avatar sx={{ width: 48, height: 48, bgcolor: 'secondary.50', color: 'secondary.main' }}><Timeline /></Avatar>
                            </Box>
                            <Box sx={{ px: 3, pb: 3, mt: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary">Progress (Module 2/8)</Typography>
                                    <Typography variant="caption" fontWeight="bold">25%</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={25} color="secondary" sx={{ height: 8, borderRadius: 4, mb: 3 }} />
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => navigate('/student-courses')}
                                    sx={{ borderRadius: 3, py: 1.2, fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', border: '2px solid rgba(0,0,0,0.1)' }}
                                >
                                    Resume
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 3. TACTICAL DEADLINES */}
            {/* 3. TACTICAL DEADLINES */}
            {/* 3. TACTICAL DEADLINES */}
            <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ color: '#ff7675' }} /> Critical Objectives (Deadlines)
                    </Typography>
                    <Button endIcon={<ArrowForward />} color="inherit" onClick={() => navigate('/student-courses')}>
                        See All
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, borderRadius: 4, borderLeft: '6px solid #ff7675', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}> {/* minWidth: 0 is crucial for flex text truncation */}
                                <Typography variant="h6" fontWeight="bold" noWrap>Physics Lab Report</Typography>
                                <Typography variant="body2" color="error" fontWeight="bold">Due Today, 23:59</Typography>
                            </Box>
                            <Button variant="contained" size="small" color="error" sx={{ borderRadius: 2, minWidth: 'fit-content' }}>Submit</Button>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, borderRadius: 4, borderLeft: '6px solid #fdcb6e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" fontWeight="bold" noWrap>History Essay</Typography>
                                <Typography variant="body2" color="text.secondary" fontWeight="bold">Due Tomorrow, 14:00</Typography>
                            </Box>
                            <Button variant="outlined" size="small" color="warning" sx={{ borderRadius: 2, minWidth: 'fit-content' }}>Draft</Button>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 3. OUTCOME SECTION: "Why am I doing this?" */}
            <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TipsAndUpdates sx={{ color: '#FDCB6E' }} /> Intel & Insights
                    </Typography>
                    <Button endIcon={<ArrowForward />} color="inherit">
                        See All
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ bgcolor: '#E8F6F6', borderRadius: 4, border: 'none', boxShadow: 0, height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight="bold" color="#00B894" gutterBottom>UPCOMING SKILL</Typography>
                                <Typography variant="h6" fontWeight="bold">Data Analysis</Typography>
                                <Typography variant="body2" color="text.secondary">Currently learning Algebra II, which unlocks Data Science basics next semester.</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ bgcolor: '#FFF0F0', borderRadius: 4, border: 'none', boxShadow: 0, height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight="bold" color="#FF7675" gutterBottom>NEXT MILESTONE</Typography>
                                <Typography variant="h6" fontWeight="bold">Mid-Term Prep</Typography>
                                <Typography variant="body2" color="text.secondary">Completing 3 more modules ensures you are 100% covered for the exam.</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ bgcolor: '#F0F3FF', borderRadius: 4, border: 'none', boxShadow: 0, height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight="bold" color="#6C5CE7" gutterBottom>CAREER PATH</Typography>
                                <Typography variant="h6" fontWeight="bold">Engineering</Typography>
                                <Typography variant="body2" color="text.secondary">Your consistent performance in Physics strongly aligns with Engineering tracks.</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>





            {/* 5. TACTICAL DATA (Additional Blocks) */}
            <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PieChart sx={{ color: '#2ecc71' }} /> Tactical Overview
                    </Typography>
                    <Button endIcon={<ArrowForward />} color="inherit">
                        See All
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ bgcolor: '#f1f2f6', borderRadius: 4, border: 'none', boxShadow: 0, height: '100%' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <MilitaryTech sx={{ fontSize: 60, color: '#ffa502' }} />
                                    <Box
                                        sx={{
                                            top: 0,
                                            left: 0,
                                            bottom: 0,
                                            right: 0,
                                            position: 'absolute',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Battle Badge Pending</Typography>
                                    <Typography variant="body2" color="text.secondary">You are 1 quiz away from the "Sharpshooter" badge.</Typography>
                                </Box>
                                <Button variant="outlined" size="small" sx={{ ml: 'auto', borderRadius: 2 }}>Claim</Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ bgcolor: '#f1f2f6', borderRadius: 4, border: 'none', boxShadow: 0, height: '100%' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <Psychology sx={{ fontSize: 60, color: '#3742fa' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Peer Support</Typography>
                                    <Typography variant="body2" color="text.secondary">2 questions in Physics need your help.</Typography>
                                </Box>
                                <Button variant="outlined" size="small" sx={{ ml: 'auto', borderRadius: 2 }}>Assist</Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 6. ACCOLADES & ACHIEVEMENTS */}
            <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmojiEvents sx={{ color: '#FFD700' }} /> Accolades
                    </Typography>
                    <Button endIcon={<ArrowForward />} color="inherit">
                        See All
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 2 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#fff9c4', color: '#fbc02d', mb: 2 }}>
                                    <Star fontSize="large" />
                                </Avatar>
                                <Typography variant="h6" fontWeight="bold">Math Wizard</Typography>
                                <Typography variant="body2" color="text.secondary">Top 5% in Algebra</Typography>
                                <Typography variant="caption" sx={{ mt: 1, color: 'text.disabled' }}>Earned 2 days ago</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 2 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#e1f5fe', color: '#03a9f4', mb: 2 }}>
                                    <School fontSize="large" />
                                </Avatar>
                                <Typography variant="h6" fontWeight="bold">Honor Roll</Typography>
                                <Typography variant="body2" color="text.secondary">Semester 1 Excellence</Typography>
                                <Typography variant="caption" sx={{ mt: 1, color: 'text.disabled' }}>Earned 1 week ago</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.02)', border: '1px dashed rgba(0,0,0,0.1)' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.disabled" fontWeight="bold">Next Reward</Typography>
                                <Typography variant="h6" color="text.secondary">Physics Master</Typography>
                                <LinearProgress variant="determinate" value={80} sx={{ mt: 1, width: 100, mx: 'auto', borderRadius: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 7. MISSION LOG (Moved to End) */}
            <Paper sx={{ p: 4, borderRadius: 5, bgcolor: '#fff', mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ color: '#57606f' }} /> Mission Log
                    </Typography>
                    <Button endIcon={<ArrowForward />} size="small" color="inherit">View Full History</Button>
                </Box>

                <List>
                    <ListItem sx={{ px: 0, py: 2, borderBottom: '1px dashed #dfe4ea' }}>
                        <ListItemIcon>
                            <Avatar sx={{ bgcolor: '#e1f5fe', color: '#03a9f4' }}>
                                <School fontSize="small" />
                            </Avatar>
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography fontWeight="bold">Completed topic "Quadratic Formulas"</Typography>}
                            secondary="Mathematics • 2 hours ago"
                        />
                        <Chip label="+10 Pts" size="small" sx={{ bgcolor: '#e1f5fe', color: '#03a9f4', fontWeight: 'bold' }} />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 2, borderBottom: '1px dashed #dfe4ea' }}>
                        <ListItemIcon>
                            <Avatar sx={{ bgcolor: '#fff9c4', color: '#fbc02d' }}>
                                <Star fontSize="small" />
                            </Avatar>
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography fontWeight="bold">Scored 100% on "Motion Quiz"</Typography>}
                            secondary="Physics • Yesterday"
                        />
                        <Chip label="+50 Pts" size="small" sx={{ bgcolor: '#fff9c4', color: '#fbc02d', fontWeight: 'bold' }} />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                            <Avatar sx={{ bgcolor: '#ffebee', color: '#ef5350' }}>
                                <Timeline fontSize="small" />
                            </Avatar>
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography fontWeight="bold">Promoted to Corporal</Typography>}
                            secondary="Rank Up • 2 days ago"
                        />
                        <Chip label="Rank Up" size="small" sx={{ bgcolor: '#ffebee', color: '#ef5350', fontWeight: 'bold' }} />
                    </ListItem>
                </List>
            </Paper>

            {/* RULES DIALOG */}
            {/* RULES DIALOG */}
            <Dialog open={openRules} onClose={() => setOpenRules(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee' }}>
                    <MilitaryTech color="primary" /> Rank & Point System
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">Ranking Structure</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Climb the ranks by accumulating GLP. New ranks unlock prestige and future customization rewards.
                            </Typography>
                            <List dense>
                                {RANKS.map((rank, i) => (
                                    <ListItem key={i}>
                                        <ListItemText
                                            primary={<Typography fontWeight="bold">{rank.icon} {rank.name}</Typography>}
                                        />
                                        <Chip label={`${rank.minPoints} Pts`} size="small" variant="outlined" />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ borderLeft: { md: '1px solid #eee' } }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">Point Rules</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Earn Global Learning Points (GLP) through completing learning activities and helping others.
                            </Typography>
                            <List>
                                {POINT_RULES.map((rule, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>{rule.icon}</ListItemIcon>
                                        <ListItemText
                                            primary={rule.action}
                                            secondary={<Typography variant="caption" color="success.main" fontWeight="bold">{rule.points} Points</Typography>}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, bgcolor: 'grey.50', p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Info color="action" />
                        <Typography variant="body2">
                            You are currently a <strong>{currentRank.name}</strong> ({MOCK_USER_POINTS} pts).
                            You need <strong>{pointsToNext} more points</strong> to be promoted to {nextRank?.name}.
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>

        </Container>
    );
};
export default StudentDashboardV3;
